import React from 'react';
import './ConfirmModalItem.scss';
import {
    EditInfo,
    EditType,
    ProppantAddOrDeletion,
    LOFAAddOrDeletion,
} from '../../../interfaces/JobProfile/ConfirmModalInterfaces';
import TextContent from '../TextContent';

const editTitleByType = (editType: EditType): string => {
    switch (editType) {
        case EditType.WELLHEAD_INFO:
            return 'Changed Well Information';
        case EditType.PROPPANT_INFO:
            return 'Changed Proppant Information';
        case EditType.PROPPANT_DELETE:
            return 'Removed Proppant';
        case EditType.PROPPANT_ADD:
            return 'Added Proppant';
        case EditType.LOFA_INFO:
            return 'Changed Loading Facility Information';
        case EditType.LOFA_ADDED:
            return 'Added Loading Facility';
        case EditType.LOFA_DELETE:
            return 'Removed Loading Facility';
        default:
            return '';
    }
};

interface AttributeTitle {
    wellName: string;
    stageTime: string;
    stages: string;
    proppantName: string;
    proppantType: string;
    volumePerBox: string;
    totalVolume: string;
    [key: string]: string;
}

const getAttributeTitle: AttributeTitle = {
    wellName: 'Well Name',
    stageTime: 'Duration/Stage (min)',
    stages: 'Stages',
    proppantName: 'Proppant Name',
    proppantType: 'Proppant Type',
    volumePerBox: 'Volume/Box',
    totalVolume: 'Total Volume',
    lofaName: 'Loading Facility',
    lofaVolume: 'Volume',
    milesFromWell: 'Miles from Well',
    sandPo: 'Sand PO',
    transportationPo: 'Transportation PO',
};
interface ConfirmModalItemProps {
    index: number;
    title: string;
    edits?: EditInfo[];
    proppantAddOrDeletions?: ProppantAddOrDeletion[];
    lofaAddOrDeletions?: LOFAAddOrDeletion[];
    editType: EditType;
}

const ConfirmModalItem = ({
    index,
    title,
    edits,
    editType,
    proppantAddOrDeletions,
    lofaAddOrDeletions,
}: ConfirmModalItemProps): React.ReactElement => {
    const isOdd = (num: number): boolean => {
        return num % 2 == 1;
    };
    const containerStyle = `ConfirmModalItem-Container ${isOdd(index) && 'ConfirmModalItem-Container-With-Background'}`;
    return (
        <div className={containerStyle}>
            <div className="ConfirmModalItem-Title">{`${index}. ${editTitleByType(editType)}`}</div>
            <div className="ConfirmModalItem-SubTitle">{title}</div>
            {edits &&
                edits.map(edit => {
                    return <EditItem edit={edit} key={edit.attributeName}></EditItem>;
                })}
            {proppantAddOrDeletions &&
                proppantAddOrDeletions.map((edit, editIndex) => <ProppantEditItem edit={edit} key={editIndex} />)}
            {lofaAddOrDeletions &&
                lofaAddOrDeletions.map((edit, editIndex) => <LofaEditItem edit={edit} key={editIndex} />)}
        </div>
    );
};

interface EditItemProps {
    edit: EditInfo;
}

const EditItem = (props: EditItemProps): React.ReactElement => {
    const { attributeName, newValue, oldValue } = props.edit;
    const oldValueTitle = oldValue === null || oldValue === undefined ? 'N/A' : oldValue.toString();
    return (
        <div className="EditItem-Container">
            <div className="EditItem-Attribute">{getAttributeTitle[attributeName]}</div>
            <div className="EditItem-Values-Container">
                <div className="EditItem-OldValue">{oldValueTitle}</div>
                <i className="material-icons ConfirmModalItem-Icon">keyboard_arrow_right</i>
                <div className="EditItem-NewValue">{newValue}</div>
            </div>
        </div>
    );
};

interface ProppantEditItemProps {
    edit: ProppantAddOrDeletion;
}

const ProppantEditItem = (props: ProppantEditItemProps): React.ReactElement => {
    const { proppant, volume, plannedVolPerBox } = props.edit;
    return (
        <div className="EditItem-Container">
            <TextContent title="Proppant" value={proppant}></TextContent>
            <TextContent title="Volume" value={`${volume}`}></TextContent>
            <TextContent title="Planned Vol/Box" value={`${plannedVolPerBox}`}></TextContent>
        </div>
    );
};

interface LofaEditItemProps {
    edit: LOFAAddOrDeletion;
}

const LofaEditItem = (props: LofaEditItemProps): React.ReactElement => {
    const { lofaName, lofaVolume, milesFromWell, maxVolumeBox, sandPo, transportationPo } = props.edit;
    return (
        <div className="EditItem-Container">
            <TextContent title="Loading Facility" value={lofaName}></TextContent>
            <TextContent title="LOFA Volume" value={`${lofaVolume}`}></TextContent>
            <TextContent title="milesFromWell" value={`${milesFromWell}`}></TextContent>
            <TextContent title="Max Volume/Box" value={`${maxVolumeBox}`}></TextContent>
            <TextContent title="Sand PO:" value={`${sandPo}`}></TextContent>
            {transportationPo !== '' && transportationPo !== null && (
                <TextContent title="Transportation PO" value={`${transportationPo}`}></TextContent>
            )}
        </div>
    );
};

export default ConfirmModalItem;

